/**
 * ALOHA PLATFORM
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from 'react';
import { Crown, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UpgradePrompt = ({ currentTier, onClose }) => {
  const navigate = useNavigate();
  
  // Don't show for users with avatar access
  if (['PRO', 'ENTERPRISE', 'LIFETIME_ENTERPRISE'].includes(currentTier)) {
    return null;
  }

  const handleUpgrade = (tier) => {
    navigate('/pricing');
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-purple-900 to-blue-900 rounded-2xl p-8 max-w-4xl mx-auto border-2 border-purple-500 relative">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition"
            data-testid="close-upgrade-prompt"
          >
            <X className="w-6 h-6" />
          </button>
        )}
        
        <div className="text-center mb-8">
          <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-4xl font-bold text-white mb-4">
            Unlock 3D Avatar Chat
          </h2>
          <p className="text-gray-300 text-lg mb-2">
            Experience photorealistic 3D avatars with real-time voice and lip sync.
          </p>
          <p className="text-yellow-400 font-semibold">
            Available exclusively for Pro and Enterprise subscribers.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white/10 rounded-xl p-6 backdrop-blur">
            <div className="flex items-center justify-between mb-4">
              <div className="text-2xl font-bold text-white">Pro</div>
              <Sparkles className="w-6 h-6 text-purple-400" />
            </div>
            <div className="text-5xl font-bold text-purple-400 mb-4">$99.99</div>
            <div className="text-gray-300 text-sm mb-4">per month</div>
            
            <ul className="text-left space-y-3 text-gray-300 mb-6">
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>3D Avatar Chat Enabled</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>Real-time Voice & Lip Sync</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>100 avatar sessions/month</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>500 minutes of avatar chat</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>Emotion Detection</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>Custom Voice Training</span>
              </li>
            </ul>
            
            <button
              onClick={() => handleUpgrade('PRO')}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white py-3 rounded-lg font-semibold transition transform hover:scale-105"
              data-testid="upgrade-to-pro-button"
            >
              Upgrade to Pro
            </button>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-6 border-2 border-yellow-500 backdrop-blur">
            <div className="flex items-center justify-between mb-4">
              <div className="text-2xl font-bold text-white">Enterprise</div>
              <Crown className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="text-5xl font-bold text-yellow-400 mb-4">$499.99</div>
            <div className="text-gray-300 text-sm mb-4">per month</div>
            
            <ul className="text-left space-y-3 text-gray-300 mb-6">
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span className="font-semibold">Unlimited Avatar Chat</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span className="font-semibold">Unlimited Sessions</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span className="font-semibold">Unlimited Minutes</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span>Multi-Avatar Support</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span>API Access</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">✓</span>
                <span>24/7 Priority Support</span>
              </li>
            </ul>
            
            <button
              onClick={() => handleUpgrade('ENTERPRISE')}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-lg font-semibold transition transform hover:scale-105"
              data-testid="upgrade-to-enterprise-button"
            >
              Upgrade to Enterprise
            </button>
          </div>
        </div>

        <p className="text-center text-gray-400 text-sm">
          Cancel anytime. No hidden fees. Full access to all features.
        </p>
      </div>
    </div>
  );
};

export default UpgradePrompt;
